import { OVERPASS_URL } from './providers'

export interface Poi {
  id: number
  lng: number
  lat: number
  name: string
  category: PoiCategory
  tags: Record<string, string>
}

export type PoiCategory = 'peak' | 'viewpoint' | 'hut' | 'water' | 'parking' | 'camping' | 'restaurant'

export const POI_CATEGORIES: { id: PoiCategory; label: string; icon: string }[] = [
  { id: 'peak', label: 'Peaks', icon: '⛰️' },
  { id: 'viewpoint', label: 'Viewpoints', icon: '🔭' },
  { id: 'hut', label: 'Huts & shelters', icon: '🏠' },
  { id: 'water', label: 'Drinking water', icon: '🚰' },
  { id: 'parking', label: 'Parking', icon: '🅿️' },
  { id: 'camping', label: 'Camping', icon: '⛺' },
  { id: 'restaurant', label: 'Food', icon: '🍽️' },
]

const QUERIES: Record<PoiCategory, string> = {
  peak: 'node["natural"="peak"]',
  viewpoint: 'node["tourism"="viewpoint"]',
  hut: 'nwr["tourism"~"alpine_hut|wilderness_hut"];nwr["amenity"="shelter"]',
  water: 'node["amenity"="drinking_water"];node["natural"="spring"]["drinking_water"="yes"]',
  parking: 'nwr["amenity"="parking"]["access"!="private"]',
  camping: 'nwr["tourism"~"camp_site|caravan_site"]',
  restaurant: 'nwr["amenity"~"restaurant|cafe|biergarten"]',
}

const cache = new Map<string, Poi[]>()

/** POIs of the selected categories in the bbox [south, west, north, east]. */
export async function fetchPois(
  bbox: [number, number, number, number],
  categories: PoiCategory[],
  signal?: AbortSignal,
): Promise<Poi[]> {
  if (!categories.length) return []
  // round the bbox so panning a few meters reuses the cache
  const key = bbox.map((v) => v.toFixed(2)).join(',') + '|' + [...categories].sort().join(',')
  const hit = cache.get(key)
  if (hit) return hit

  const bboxStr = bbox.map((v) => v.toFixed(4)).join(',')
  const parts = categories
    .flatMap((c) => QUERIES[c].split(';'))
    .map((q) => `${q}(${bboxStr});`)
    .join('')
  const query = `[out:json][timeout:20];(${parts});out center 250;`
  const res = await fetch(OVERPASS_URL, { method: 'POST', body: 'data=' + encodeURIComponent(query), signal })
  if (!res.ok) return []
  const data = (await res.json()) as {
    elements?: { id: number; lat?: number; lon?: number; center?: { lat: number; lon: number }; tags?: Record<string, string> }[]
  }

  const pois: Poi[] = []
  for (const el of data.elements ?? []) {
    const lat = el.lat ?? el.center?.lat
    const lon = el.lon ?? el.center?.lon
    if (lat == null || lon == null) continue
    const tags = el.tags ?? {}
    pois.push({
      id: el.id,
      lng: lon,
      lat,
      name: tags.name ?? defaultName(tags),
      category: categorize(tags),
      tags,
    })
  }
  cache.set(key, pois)
  if (cache.size > 60) cache.delete(cache.keys().next().value!)
  return pois
}

function categorize(tags: Record<string, string>): PoiCategory {
  if (tags.natural === 'peak') return 'peak'
  if (tags.tourism === 'viewpoint') return 'viewpoint'
  if (tags.tourism === 'alpine_hut' || tags.tourism === 'wilderness_hut' || tags.amenity === 'shelter') return 'hut'
  if (tags.amenity === 'drinking_water' || tags.natural === 'spring') return 'water'
  if (tags.amenity === 'parking') return 'parking'
  if (tags.tourism === 'camp_site' || tags.tourism === 'caravan_site') return 'camping'
  return 'restaurant'
}

function defaultName(tags: Record<string, string>): string {
  const cat = categorize(tags)
  const ele = tags.ele ? ` (${tags.ele} m)` : ''
  return POI_CATEGORIES.find((c) => c.id === cat)!.label.replace(/s$| & .*/, '') + ele
}
